import { useEffect, useCallback, useState, useRef } from "react";

const useInactivityTimeout = ({
  timeoutMinutes = 30,
  warningThresholdMinutes = 5,
  onWarning,
  onTimeout,
  enabled = true,
}: UseInactivityTimeoutOptions = {}) => {
  const [isWarningActive, setIsWarningActive] = useState(false);

  const timeoutTimerRef = useRef<NodeJS.Timeout>();
  const lastActivityRef = useRef<number>(Date.now());
  const isWarningActiveRef = useRef(false);
  const lastMouseMoveRef = useRef<number>(0);

  // Convert minutes to milliseconds
  const timeoutMs = Math.floor(timeoutMinutes * 60 * 1000);
  const warningMs = Math.floor(warningThresholdMinutes * 60 * 1000);

  const checkInactivity = useCallback(() => {
    // First check if enabled
    if (!enabled) {
      return;
    }

    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;

    // Check if we should show warning
    if (timeSinceLastActivity >= timeoutMs - warningMs) {
      if (!isWarningActiveRef.current) {
        isWarningActiveRef.current = true;
        setIsWarningActive(true);
        onWarning?.();
      }
    }

    // Check if we should timeout
    if (timeSinceLastActivity >= timeoutMs) {
      // timeout();
      onTimeout?.();
      return;
    }

    // Schedule next check
    timeoutTimerRef.current = setTimeout(checkInactivity, 2000);
  }, [timeoutMs, warningMs, enabled, onWarning, onTimeout]);

  const resetInactivityTimer = useCallback(() => {
    if (!enabled) {
      return;
    }

    lastActivityRef.current = Date.now();

    if (isWarningActiveRef.current) {
      isWarningActiveRef.current = false;
      setIsWarningActive(false);
    }
  }, [enabled]);

  useEffect(() => {
    // Only start timers if enabled
    if (!enabled) {
      return;
    }

    const regularEvents = [
      "keypress",
      "keydown",
      "click",
      "scroll",
      "mousedown",
      "touchstart",
      "wheel",
    ];

    const handleRegularActivity = () => {
      resetInactivityTimer();
    };

    const handleMouseMove = () => {
      const now = Date.now();
      if (now - lastMouseMoveRef.current > 1000) {
        lastMouseMoveRef.current = now;
        resetInactivityTimer();
      }
    };

    regularEvents.forEach((event) => {
      window.addEventListener(event, handleRegularActivity, { passive: true });
    });

    window.addEventListener("mousemove", handleMouseMove, { passive: true });

    // Start the inactivity check
    timeoutTimerRef.current = setTimeout(checkInactivity, 2000);

    return () => {
      regularEvents.forEach((event) => {
        window.removeEventListener(event, handleRegularActivity);
      });
      window.removeEventListener("mousemove", handleMouseMove);
      clearTimeout(timeoutTimerRef.current);
    };
  }, [checkInactivity, resetInactivityTimer, timeoutMs, warningMs, enabled]);

  return {
    isWarningActive,
    resetTimer: resetInactivityTimer,
  };
};

export default useInactivityTimeout;

interface UseInactivityTimeoutOptions {
  timeoutMinutes?: number;
  warningThresholdMinutes?: number;
  onWarning?: () => void;
  onTimeout?: () => void;
  enabled?: boolean;
}
